import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { css } from '@compiled/react';

import Heading from '@atlaskit/heading/heading';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';

import { AGENT_CARDS } from './agentCards.config.ts';
import { AgentCardRow } from './AgentCardRow.tsx';
import { i18n } from './messages.ts';

const sectionStyles = css({
	marginBottom: token('space.300'),
});

const headerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	marginBottom: token('space.150'),
});

/**
 * "Rovo Agents for you" section on Confluence Home / For You.
 *
 * PR1 (CCCEMAU-3070): scaffold — section shell, header and Labs lozenge.
 * PR2 (CCCEMAU-3070): static card UI + responsive layout.
 * PR3 (CCCEMAU-3070): dismiss is functional — dismissing a card removes it from
 * the view and the row reflows (3 → 2 → 1 cards). When every card is dismissed
 * the whole section unmounts so the next section takes its place. Persistence of
 * dismissals across reloads is a follow-up.
 *
 * Eligibility/gating and grid + UFOSegment placement are owned by ForYou.tsx,
 * which only mounts this component when eligible.
 */
export const RovoAgentsForYou: FC = () => {
	const [dismissedKeys, setDismissedKeys] = useState<ReadonlySet<string>>(() => new Set());

	const handleDismiss = useCallback((key: string) => {
		setDismissedKeys((prev) => {
			const next = new Set(prev);
			next.add(key);
			return next;
		});
	}, []);

	const visibleCards = useMemo(
		() => AGENT_CARDS.filter((card) => !dismissedKeys.has(card.key)),
		[dismissedKeys],
	);

	// All cards dismissed — unmount the section so the following section takes the space.
	if (visibleCards.length === 0) {
		return null;
	}

	return (
		<section css={sectionStyles} data-testid="rovo-agents-for-you">
			<div css={headerStyles}>
				<Heading size="xsmall" as="h2">
					<FormattedMessage {...i18n.sectionTitle} />
				</Heading>
				<Lozenge appearance="new">
					<FormattedMessage {...i18n.labsLozenge} />
				</Lozenge>
			</div>
			<AgentCardRow cards={visibleCards} onDismiss={handleDismiss} />
		</section>
	);
};
